import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Upload, FileText, Loader2, AlertCircle, CheckCircle } from "lucide-react";

interface PerformanceImportProps {
  onImportComplete: () => void;
}

interface ImportResult {
  total: number;
  successful: number;
  failed: number;
  errors: string[];
}

export const PerformanceImport = ({ onImportComplete }: PerformanceImportProps) => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setResult(null);
    } else {
      toast.error(language === 'ar' ? 'يرجى تحديد ملف CSV' : 'Please select a CSV file');
    }
  };

  const parseCSV = (text: string): any[] => {
    const lines = text.split('\n').filter(line => line.trim());
    const headers = lines[0].split(',').map(h => h.trim());
    
    return lines.slice(1).map(line => {
      const values = line.split(',').map(v => v.trim());
      const obj: any = {};
      headers.forEach((header, index) => {
        obj[header] = values[index];
      });
      return obj;
    });
  };

  const handleImport = async () => {
    if (!file || !user) return;

    setImporting(true);
    const importResult: ImportResult = {
      total: 0,
      successful: 0,
      failed: 0,
      errors: []
    };

    try {
      const text = await file.text();
      const data = parseCSV(text);
      importResult.total = data.length;

      for (const row of data) {
        try {
          // Expected CSV columns: campaign_id, reach, engagement, spent
          const campaignId = row.campaign_id || row.Campaign_ID || row.CampaignID;
          const reach = parseInt(row.reach || row.Reach || '0');
          const engagement = parseFloat(row.engagement || row.Engagement || '0');
          const spent = parseFloat(row.spent || row.Spent || '0');

          if (!campaignId) {
            importResult.failed++;
            importResult.errors.push(`Missing campaign_id in row`);
            continue;
          }

          // Find campaign by campaign_id
          const { data: campaigns, error: findError } = await supabase
            .from('saved_campaigns')
            .select('id')
            .eq('campaign_id', campaignId)
            .limit(1);

          if (findError || !campaigns || campaigns.length === 0) {
            importResult.failed++;
            importResult.errors.push(`Campaign not found: ${campaignId}`);
            continue;
          }

          // Update performance data
          const { error: updateError } = await supabase
            .from('saved_campaigns')
            .update({
              actual_reach: reach,
              actual_engagement: engagement,
              actual_spent: spent,
              performance_updated_at: new Date().toISOString(),
              performance_updated_by: user.id
            })
            .eq('id', campaigns[0].id);

          if (updateError) {
            importResult.failed++;
            importResult.errors.push(`Failed to update ${campaignId}: ${updateError.message}`);
          } else {
            importResult.successful++;
          }
        } catch (error) {
          importResult.failed++;
          importResult.errors.push(`Error processing row: ${error}`);
        }
      }

      setResult(importResult);
      
      if (importResult.successful > 0) {
        toast.success(
          language === 'ar' 
            ? `تم استيراد ${importResult.successful} حملة بنجاح` 
            : `Successfully imported ${importResult.successful} campaigns`
        );
        onImportComplete();
      }

      if (importResult.failed > 0) {
        toast.warning(
          language === 'ar'
            ? `فشل استيراد ${importResult.failed} حملة`
            : `Failed to import ${importResult.failed} campaigns`
        );
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(language === 'ar' ? 'فشل الاستيراد' : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6 mt-4">
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {language === 'ar' 
            ? 'يجب أن يحتوي ملف CSV على الأعمدة التالية: campaign_id, reach, engagement, spent'
            : 'CSV file must contain the following columns: campaign_id, reach, engagement, spent'}
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="csv-file">
            {language === 'ar' ? 'اختر ملف CSV' : 'Select CSV File'}
          </Label>
          <div className="flex items-center gap-4">
            <Input
              id="csv-file"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              disabled={importing}
            />
            {file && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileText className="h-4 w-4" />
                {file.name}
              </div>
            )}
          </div>
        </div>

        <Button
          onClick={handleImport}
          disabled={!file || importing}
          className="w-full gap-2"
        >
          {importing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {language === 'ar' ? 'جاري الاستيراد...' : 'Importing...'}
            </>
          ) : (
            <>
              <Upload className="h-4 w-4" />
              {language === 'ar' ? 'استيراد البيانات' : 'Import Data'}
            </>
          )}
        </Button>
      </div>

      {result && (
        <Alert variant={result.failed === 0 ? "default" : "destructive"}>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-semibold">
                {language === 'ar' ? 'نتائج الاستيراد:' : 'Import Results:'}
              </p>
              <ul className="text-sm space-y-1">
                <li>{language === 'ar' ? 'إجمالي' : 'Total'}: {result.total}</li>
                <li className="text-green-600">
                  {language === 'ar' ? 'نجح' : 'Successful'}: {result.successful}
                </li>
                <li className="text-red-600">
                  {language === 'ar' ? 'فشل' : 'Failed'}: {result.failed}
                </li>
              </ul>
              {result.errors.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-sm font-medium">
                    {language === 'ar' ? 'عرض الأخطاء' : 'View Errors'}
                  </summary>
                  <ul className="mt-2 text-xs space-y-1 max-h-40 overflow-y-auto">
                    {result.errors.map((error, index) => (
                      <li key={index} className="text-red-600">• {error}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      <div className="rounded-lg border p-4 bg-muted/50">
        <h4 className="font-semibold mb-2">
          {language === 'ar' ? 'مثال على تنسيق CSV:' : 'CSV Format Example:'}
        </h4>
        <pre className="text-xs bg-background p-3 rounded overflow-x-auto">
          campaign_id,reach,engagement,spent{'\n'}
          123e4567-e89b-12d3-a456-426614174000,50000,4.5,15000{'\n'}
          223e4567-e89b-12d3-a456-426614174001,75000,3.8,22000
        </pre>
      </div>
    </div>
  );
};
